import { AnimatePresence, motion } from 'motion/react'
import React, { useState } from 'react'
import { classNames } from 'src/lib/utils/helpers'
import { Icon, ShopfloMainLogo, getTextColorByBrightness } from '@shopflo/ui'
import { Receipt, CaretUp, Plus } from '@phosphor-icons/react'
import { currencyFormatter } from 'src/lib/utils/formatters'
import UPICartIcons from 'src/assets/images/cart-upi-icons.svg'
import { CartBanner } from './CartBanner'
import { QuickAddOnAddonLevel } from 'src/lib/types/quickAddOnV2'
import { ShopfloImageDomain } from 'src/lib/utils/constants'
import { CartFeatureHOC } from '../cartV2/CartFeatureHOC'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { customizeCartUIType } from 'src/lib/types/cartUI'
import { BannerConfigType } from 'src/lib/types/customizeUI'

// Simplified AddOnButton component for preview
const AddOnButton = ({
  addOnsList,
  addonConfig,
  merchantPrimaryColor
}: {
  addOnsList: Array<{ addon_id: string; product_name: string; price: number; image?: string }>
  addonConfig: customizeCartUIType['addonConfig']
  merchantPrimaryColor?: string
}) => {
  return (
    <div
      className='py-1.5'
      style={{
        backgroundColor: addonConfig.backgroundColor
      }}
    >
      {addOnsList.map(item => (
        <motion.div key={item.addon_id} layout>
          <div
            className='flex items-center gap-2.5 px-5 py-1 cursor-pointer transition-opacity hover:opacity-90'
            style={{
              color: addonConfig.textColor
            }}
          >
            <div className='flex h-9 w-9 items-center justify-center rounded-md'>
              <img
                src={item.image || `${ShopfloImageDomain}/package.png`}
                alt={item.product_name}
                className='h-full w-full rounded-md object-cover'
              />
            </div>

            {/* Content Section */}
            <div className='flex flex-1 flex-col gap-0'>
              <div className='flex items-center gap-1'>
                <span className='text-text-xs-semibold' style={{ color: addonConfig.textColor }}>
                  {item.product_name}
                </span>
              </div>
              <span
                className='text-text-xs-medium text-zinc-500'
                style={{
                  color: addonConfig.textColor,
                  opacity: 0.6
                }}
              >
                {currencyFormatter(item.price)}
              </span>
            </div>

            <button className='flex items-center gap-0.5 rounded-[5.6px] px-1 py-0.5'>
              <div className='flex h-3 w-3 items-center justify-center rounded-[5.6px]'>
                <Icon
                  IconComponent={Plus}
                  size='xxs'
                  iconProps={{ weight: 'bold', color: addonConfig.buttonColor ?? merchantPrimaryColor ?? '#000000' }}
                />
              </div>
              <span
                className='text-text-sm-semibold'
                style={{ color: addonConfig.buttonColor ?? merchantPrimaryColor ?? '#000000' }}
              >
                Add
              </span>
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  )
}

interface CartFooterProps {
  footerConfig: customizeCartUIType['footer']
  addonConfig: customizeCartUIType['addonConfig']
  primaryCTAConfig: customizeCartUIType['primaryCTAConfig']
  merchantPrimaryColor?: string
  quickAddOnTable: any[]
  cartBannerUIConfigData: BannerConfigType
  cartBottomBannerUIConfigData: BannerConfigType
  enableShadow?: boolean
  showUpiIconsOnCartButton: boolean
}

export const CartFooter: React.FC<CartFooterProps> = ({
  footerConfig,
  addonConfig,
  primaryCTAConfig,
  merchantPrimaryColor,
  quickAddOnTable,
  cartBannerUIConfigData,
  cartBottomBannerUIConfigData,
  enableShadow = false,
  showUpiIconsOnCartButton
}) => {
  const cartLevelAddons = quickAddOnTable.filter(
    addon =>
      addon.addonLevel === QuickAddOnAddonLevel.CART &&
      ['DEFAULT', 'CART'].includes(addon.uiAsset) &&
      addon.status === true
  )

  const gradient = `linear-gradient(to right, ${footerConfig.savingsColor}, ${footerConfig.savingsColor}, #000000, ${footerConfig.savingsColor}, ${footerConfig.savingsColor})`

  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)
  const billing = {
    total_payable: 2450,
    sub_total: 2950
  }
  const totalDiscountedValue = 500

  const addOnsList = cartLevelAddons
    .filter(addon => addon.metadata?.product?.[0]) // Only show addons with product data
    .slice(0, 1)
    .map(addon => ({
      addon_id: addon.id,
      product_name: addon.metadata?.product?.[0]?.description || 'Add-on',
      price: addon.metadata?.product?.[0]?.variants?.[0]?.price || 0,
      image: addon.metadata?.product?.[0]?.imageUrl
    }))

  const getCartSummary = () => {
    // Hardcoded values for cart summary
    const originalSubTotal = 3500
    const deliveryFee = 50
    const discountOnMRP = 550
    const additionalDiscount = 500
    const grandTotal = billing.total_payable

    const renderRow = (label: string, value: string, isDiscount?: boolean) => {
      return (
        <div className='relative flex items-center justify-between gap-2 px-1 py-1.5'>
          <div
            className='absolute top-0 left-0 right-0 border-t-2 border-dotted border-zinc-900/10'
            style={{
              borderColor: getTextColorByBrightness({
                bgHexColor: footerConfig.backgroundColor,
                lightOpacity: 24
              })
            }}
          />
          <span
            className='text-[12px] font-medium leading-[18px] text-zinc-600'
            style={{ color: footerConfig.textColor, opacity: 0.8 }}
          >
            {label}
          </span>
          <span
            className='text-[12px] font-medium leading-[18px] text-right'
            style={{ color: isDiscount ? footerConfig.savingsColor : footerConfig.textColor, opacity: 0.8 }}
          >
            {value}
          </span>
        </div>
      )
    }

    return (
      <div className='flex flex-col'>
        {renderRow('Total MRP', currencyFormatter(originalSubTotal))}
        {renderRow('Delivery Fee', currencyFormatter(deliveryFee))}
        {renderRow('Discount on MRP', currencyFormatter(discountOnMRP), true)}
        {renderRow('Discount', currencyFormatter(additionalDiscount), true)}
        {renderRow('Grand Total', currencyFormatter(grandTotal))}
        {totalDiscountedValue > 0 && (
          <div
            className='mt-1 flex items-center justify-center gap-2 border-t-2 border-dotted border-zinc-900/10 min-h-12'
            style={{
              backgroundImage: `linear-gradient(90deg, transparent 0%, ${hexToRGBA(
                footerConfig.savingsColor,
                0.17
              )} 50%, transparent 100%)`
            }}
          >
            <p
              className='px-1 text-center text-[12px] font-semibold leading-[18px]'
              style={{ color: footerConfig.savingsColor }}
            >
              You Saved {currencyFormatter(totalDiscountedValue)} (
              {((totalDiscountedValue / originalSubTotal) * 100).toFixed(0)}%) so far!
            </p>
          </div>
        )}
      </div>
    )
  }

  const getCartTotal = () => {
    return (
      <div className='w-full flex justify-between items-center pb-2'>
        <div className='flex items-center gap-1'>
          <Icon
            className='text-zinc-900'
            IconComponent={Receipt}
            size='sm'
            iconProps={{ weight: 'fill', color: footerConfig.textColor, opacity: 0.4 }}
          />
          <div className='text-text-sm-medium text-zinc-700' style={{ color: footerConfig.textColor }}>
            Estimated Total
          </div>
        </div>
        <div className='flex items-start gap-1'>
          <div className='flex flex-col items-end'>
            {/* Price component - hardcoded version */}
            <div className='flex items-center gap-1'>
              {billing?.sub_total && billing.sub_total > billing.total_payable && (
                <span
                  className='text-text-xs-medium text-zinc-900 line-through'
                  style={{ color: footerConfig.textColor, opacity: 0.6 }}
                >
                  {currencyFormatter(billing.sub_total)}
                </span>
              )}
              <span className='text-text-lg-semibold text-zinc-900' style={{ color: footerConfig.textColor }}>
                {currencyFormatter(billing?.total_payable)}
              </span>
            </div>
            {totalDiscountedValue > 0 && (
              <div
                key={footerConfig.savingsColor}
                className='text-text-xs-semibold animate-gradient-sweep'
                style={
                  {
                    display: 'inline-block',
                    background: gradient,
                    backgroundSize: '200% 100%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    MozBackgroundClip: 'text',
                    MozTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    color: 'transparent'
                  } as React.CSSProperties
                }
              >
                <span className='text-text-xs-medium'>You saved</span>{' '}
                <span>{currencyFormatter(totalDiscountedValue)}!</span>
              </div>
            )}
          </div>
          <span className='mt-2'>
            <Icon
              IconComponent={CaretUp}
              size='xxs'
              className={classNames('transition-transform duration-300', isSummaryExpanded ? 'rotate-180' : '')}
              iconProps={{ weight: 'bold', color: hexToRGBA(footerConfig.textColor, 0.6) }}
            />
          </span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <CartFeatureHOC element='ADD_ON' hideBorder>
        {addOnsList?.length > 0 && (
          <motion.div
            layout
            initial={{ height: 'auto', opacity: 1 }}
            animate={{
              height: isSummaryExpanded ? 0 : 'auto',
              opacity: isSummaryExpanded ? 0 : 1
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
              mass: 1
            }}
            className='overflow-hidden'
          >
            <AddOnButton
              addOnsList={addOnsList}
              addonConfig={addonConfig}
              merchantPrimaryColor={merchantPrimaryColor}
            />
          </motion.div>
        )}
      </CartFeatureHOC>
      <CartFeatureHOC element='BOTTOM_BANNER' hideBorder>
        <CartBanner
          bannerPosition='FOOTER'
          cartBannerUIConfigData={cartBannerUIConfigData}
          cartBottomBannerUIConfigData={cartBottomBannerUIConfigData}
        />
      </CartFeatureHOC>
      <div
        style={{
          boxShadow: enableShadow ? '0px -1px 5px 0px rgba(175, 175, 175, 0.3)' : 'none',
          position: 'relative'
        }}
      >
        <div
          className={classNames('flex flex-col px-4 space-y-3 pt-2 [&>*:last-child]:!mt-0')}
          style={{
            backgroundColor: footerConfig.backgroundColor,
            color: footerConfig.textColor
          }}
        >
          <CartFeatureHOC element='FOOTER' hideBorder>
            <div className='item-center flex flex-col justify-between'>
              <div className='w-full'>
                <div className='cursor-pointer' onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}>
                  {getCartTotal()}
                </div>
                <AnimatePresence>
                  {isSummaryExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30,
                        mass: 1
                      }}
                      className='overflow-hidden'
                    >
                      {getCartSummary()}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </CartFeatureHOC>
          <CartFeatureHOC element='PRIMARY_BUTTON' hideBorder>
            <button
              className={classNames(
                'flex py-1.5 flex-row items-center gap-2 whitespace-nowrap cursor-pointer px-4 text-base rounded-xl h-14 w-full justify-center text-white text-text-md-semibold',
                showUpiIconsOnCartButton ? 'justify-between' : 'justify-center'
              )}
              style={{
                backgroundColor: primaryCTAConfig.backgroundColor ?? merchantPrimaryColor ?? '#000000',
                color: primaryCTAConfig.textColor,
                borderRadius: primaryCTAConfig.borderRadius
              }}
            >
              <span>{primaryCTAConfig.cartButtonCTAText}</span>
              {showUpiIconsOnCartButton && <UPICartIcons className='w-auto h-10' />}
            </button>
          </CartFeatureHOC>
        </div>
        <div
          className='flex justify-center items-center'
          style={{
            backgroundColor: footerConfig.backgroundColor
          }}
        >
          <ShopfloBadge footerBackgroundColor={footerConfig.backgroundColor} />
        </div>
      </div>
    </div>
  )
}

interface ShopfloBadgeProps {
  footerBackgroundColor: string
}

export const ShopfloBadge: React.FC<ShopfloBadgeProps> = ({ footerBackgroundColor }) => {
  const color = getTextColorByBrightness({
    bgHexColor: footerBackgroundColor ?? '#FFFFFF',
    darkOpacity: 50,
    lightOpacity: 40
  })
  return (
    <div className={`flex flex-row items-center my-2.5 space-x-1`}>
      <span
        className='text-text-xxs-medium text-zinc-400'
        style={{
          color: color
        }}
      >
        Powered by
      </span>
      <ShopfloMainLogo className='h-3' color={color} />
    </div>
  )
}
